import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import { X, Search, Link2, Ban, AlertTriangle, Copy, Trash2 } from 'lucide-react';
import clsx from 'clsx';

interface IssueRelationModalProps {
  issueId: string;
  projectId: string;
  existingRelations?: any[];
  onClose: () => void;
  onSubmit: (relationType: string, relatedIssueId: string) => void;
  onDelete?: (relationId: string) => void;
}

const RELATION_TYPES = [
  { value: 'blocked_by', label: 'Blocked by', description: 'This issue is blocked by', icon: AlertTriangle, color: 'text-orange-600', bgColor: 'bg-orange-50' },
  { value: 'blocking', label: 'Blocking', description: 'This issue is blocking', icon: Ban, color: 'text-red-600', bgColor: 'bg-red-50' },
  { value: 'relates_to', label: 'Relates to', description: 'This issue relates to', icon: Link2, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  { value: 'duplicate_of', label: 'Duplicate of', description: 'This issue is a duplicate of', icon: Copy, color: 'text-purple-600', bgColor: 'bg-purple-50' },
];

export function IssueRelationModal({ issueId, projectId, existingRelations = [], onClose, onSubmit, onDelete }: IssueRelationModalProps) {
  const api = useApi();
  const [relationType, setRelationType] = useState('blocked_by');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIssue, setSelectedIssue] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'existing' | 'add'>(existingRelations.length > 0 ? 'existing' : 'add');

  const hasExistingRelations = existingRelations.length > 0;

  const { data: workItems } = useQuery({
    queryKey: ['workitems', projectId],
    queryFn: () => api.getWorkItems(projectId),
    enabled: !!projectId,
  });

  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId),
    enabled: !!projectId,
  });

  // Get IDs of already related issues
  const alreadyRelatedIds = useMemo(() => {
    return new Set(existingRelations.map((r: any) => r.bh_related_issue));
  }, [existingRelations]);

  // Filter out current issue, already related issues, and apply search
  const filteredIssues = useMemo(() => {
    if (!workItems) return [];
    return workItems
      .filter((item: any) => item.bh_workitemid !== issueId)
      .filter((item: any) => !alreadyRelatedIds.has(item.bh_workitemid))
      .filter((item: any) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
          item.bh_name.toLowerCase().includes(query) ||
          `#${item.bh_sequenceid}`.includes(query)
        );
      });
  }, [workItems, issueId, searchQuery, alreadyRelatedIds]);

  // Group existing relations by type
  const groupedRelations = useMemo(() => {
    return existingRelations.reduce((acc: any, relation: any) => {
      const type = relation.bh_relation_type;
      if (!acc[type]) acc[type] = [];
      acc[type].push(relation);
      return acc;
    }, {});
  }, [existingRelations]);

  const getWorkItemById = (id: string) => {
    return workItems?.find((wi: any) => wi.bh_workitemid === id);
  };

  const getStateById = (stateId: string) => {
    return states?.find((s: any) => s.bh_stateid === stateId);
  };

  const handleSubmit = () => {
    if (selectedIssue) {
      onSubmit(relationType, selectedIssue);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-900">
                {hasExistingRelations ? 'Manage Relations' : 'Add Relation'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs when there are existing relations */}
          {hasExistingRelations && (
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('existing')}
                className={clsx(
                  'flex-1 px-4 py-2.5 text-sm font-medium transition-colors',
                  activeTab === 'existing'
                    ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                )}
              >
                Current ({existingRelations.length})
              </button>
              <button
                onClick={() => setActiveTab('add')}
                className={clsx(
                  'flex-1 px-4 py-2.5 text-sm font-medium transition-colors',
                  activeTab === 'add'
                    ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                )}
              >
                Add New
              </button>
            </div>
          )}

          {/* Existing Relations Tab */}
          {activeTab === 'existing' && hasExistingRelations && (
            <div className="flex-1 overflow-y-auto p-3">
              <div className="space-y-3">
                {RELATION_TYPES.map((typeConfig) => {
                  const relations = groupedRelations[typeConfig.value] || [];
                  if (relations.length === 0) return null;

                  const Icon = typeConfig.icon;

                  return (
                    <div key={typeConfig.value} className={clsx('rounded-lg p-3', typeConfig.bgColor)}>
                      <div className={clsx('flex items-center gap-2 mb-2', typeConfig.color)}>
                        <Icon className="w-4 h-4" />
                        <span className="text-sm font-semibold">{typeConfig.description}</span>
                      </div>
                      <div className="space-y-2">
                        {relations.map((relation: any) => {
                          const relatedItem = getWorkItemById(relation.bh_related_issue);
                          if (!relatedItem) return null;
                          const state = getStateById(relatedItem._bh_state_value);

                          return (
                            <div
                              key={relation.bh_relationid}
                              className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-gray-100 group"
                            >
                              <span
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: state?.bh_color || '#6b7280' }}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm text-gray-800 font-medium truncate">
                                  <span className={clsx('font-mono text-xs mr-1.5', typeConfig.color)}>
                                    #{relatedItem.bh_sequenceid}
                                  </span>
                                  {relatedItem.bh_name}
                                </div>
                                <span className="text-xs text-gray-500">
                                  {state?.bh_name || 'Unknown'}
                                </span>
                              </div>
                              {onDelete && (
                                <button
                                  onClick={() => onDelete(relation.bh_relationid)}
                                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                                  title="Remove relation"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add New Tab Content */}
          {activeTab === 'add' && (
            <>
              {/* Relation Type */}
              <div className="px-4 py-3 border-b border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Relation Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {RELATION_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        onClick={() => setRelationType(type.value)}
                        className={clsx(
                          'px-3 py-2 rounded-lg text-sm font-medium text-left transition-colors flex items-center gap-2',
                          relationType === type.value
                            ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                            : 'bg-gray-50 text-gray-700 border-2 border-transparent hover:bg-gray-100'
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Search */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search issues by name or ID..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    autoFocus={activeTab === 'add'}
                  />
                </div>
              </div>

              {/* Issue List */}
              <div className="flex-1 overflow-y-auto p-2">
                {filteredIssues.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    {searchQuery ? 'No issues found' : alreadyRelatedIds.size > 0 ? 'All other issues already have relations' : 'No other issues in this project'}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredIssues.map((item: any) => {
                      const state = getStateById(item._bh_state_value);
                      return (
                        <button
                          key={item.bh_workitemid}
                          onClick={() => setSelectedIssue(item.bh_workitemid)}
                          className={clsx(
                            'w-full px-3 py-2 rounded-lg text-left flex items-center gap-3 transition-colors',
                            selectedIssue === item.bh_workitemid
                              ? 'bg-primary-50 border-2 border-primary-500'
                              : 'hover:bg-gray-50 border-2 border-transparent'
                          )}
                        >
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: state?.bh_color || '#6b7280' }}
                          />
                          <span className="text-xs text-gray-500 font-mono">
                            #{item.bh_sequenceid}
                          </span>
                          <span className="text-sm text-gray-900 truncate flex-1">
                            {item.bh_name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              {activeTab === 'existing' ? 'Done' : 'Cancel'}
            </button>
            {activeTab === 'add' && (
              <button
                onClick={handleSubmit}
                disabled={!selectedIssue}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Relation
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
